import React, { useEffect, useRef, useState } from 'react'
import { Link } from '@inertiajs/react'
import { X } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { apiFetch } from '@/lib/csrf'
import { announcementIcon } from '@/lib/announcements'
import type { AnnouncementData } from '@/types/shared'

// THE SHELL (docs/plans/2026-08-14-product-announcements.md, decision 5). The announcement is data;
// this component is the fixed, reviewed frame it is poured into — so brand, vocabulary, link rules
// and the design system stay enforceable in ONE place for every announcement that will ever ship.
//
// It ANNOUNCES, it does not interrupt (decision 4): non-modal, no backdrop, bottom-left of the
// content area, the page fully usable behind it. It is not a dialog and it never becomes one, and
// it never enters the WarningBanner rail — that rail means a fault, and news is the opposite of a
// fault (decision 10).
//
// THE ART IS A GUEST (decisions 5 + 6). `coverHtml` is a self-contained document rendered inside
// `<iframe srcDoc sandbox="allow-scripts">` — no `allow-same-origin` (so it cannot touch our cookies,
// storage or DOM), no top-navigation, no external requests — inside a wrapper carrying
// `pointer-events-none`, which makes it strictly non-interactive. Every interaction on this card
// lives in the two CTAs below. A missing cover falls back to a brand gradient: failing art must
// never take the message down.
//
// EVERY HREF IS BACKEND-SERVED. This file appends nothing to any of them.
//
// THE CENSUS (§12). Three beacons — `seen` when the card mounts, `learn_more_click`, `cta_click` —
// posted to the backend-served `eventsHref` and recorded first-occurrence-only server-side. They
// are strictly fire-and-forget: never awaited, never blocking a navigation, every failure
// swallowed. A tracking outage must be invisible to the operator, and nothing the beacons write
// changes what this card does — dismissal alone decides that (§12 rule 3).

interface AnnouncementCardProps {
  announcement: AnnouncementData
}

/**
 * The 12:5 art box. Same aspect and same wrapper whether the cover exists or not, so the card
 * does not resize when an announcement ships without art.
 */
function AnnouncementCover({ coverHtml, title }: { coverHtml: string | null; title: string }) {
  const box = 'pointer-events-none relative w-full overflow-hidden bg-slate-900'
  const aspect = { aspectRatio: '12 / 5' }

  if (!coverHtml) {
    return (
      <div className={box} style={aspect} data-testid="announcement-cover-fallback" aria-hidden="true">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 140% at 12% 18%, rgba(13,148,136,0.85) 0%, rgba(13,148,136,0.15) 45%, rgba(15,23,42,0) 70%), linear-gradient(135deg, #0f172a 0%, #134e4a 100%)',
          }}
        />
        {/* The wordmark's three bars, oversized — recognisably ours without a literal anywhere. */}
        <div className="absolute bottom-5 left-6 flex flex-col gap-2">
          <span className="block h-1.5 w-16 rounded-full bg-white/90" />
          <span className="block h-1.5 w-11 rounded-full bg-white/60" />
          <span className="block h-1.5 w-7 rounded-full bg-white/35" />
        </div>
      </div>
    )
  }

  return (
    <div className={box} style={aspect}>
      <iframe
        title={`${title} — cover animation`}
        srcDoc={coverHtml}
        sandbox="allow-scripts"
        scrolling="no"
        className="absolute inset-0 h-full w-full border-0"
      />
    </div>
  )
}

export default function AnnouncementCard({ announcement }: AnnouncementCardProps) {
  const [hidden, setHidden] = useState(false)

  // ONE dismissal, fired by the ✕ AND by both CTAs — a click on a CTA is "message delivered"
  // (§4.2). It is deliberately fire-and-forget: the row it writes is idempotent server-side, and a
  // failed POST must never block the navigation the operator asked for or surface an error about a
  // card they were already done with. The worst case is the card reappearing on the next page load.
  function fireDismissal() {
    try {
      void apiFetch(announcement.dismissHref, { method: 'POST' }).catch(() => {})
    } catch {
      /* never let dismissal bookkeeping reach the operator */
    }
  }

  // The census beacon (§12). Same fire-and-forget discipline as the dismissal, and for a stricter
  // reason: this one buys the operator nothing at all, so it is never allowed to cost them
  // anything either — no await, no error surface, no effect on what renders.
  function fireEvent(event: 'seen' | 'learn_more_click' | 'cta_click') {
    try {
      void apiFetch(announcement.eventsHref, { method: 'POST', body: JSON.stringify({ event }) }).catch(() => {})
    } catch {
      /* a census is never worth an operator-visible failure */
    }
  }

  // `seen` fires ONCE per card, not once per render: the ref makes a re-render (dismissing, a
  // parent update) a no-op, and the announcement id in the dependency list re-arms it only when a
  // genuinely different card mounts. The server-side upsert makes a duplicate harmless anyway —
  // this keeps the request off the wire, which is the part that costs anything.
  const seenFor = useRef<number | null>(null)
  useEffect(() => {
    if (seenFor.current === announcement.id) return
    seenFor.current = announcement.id
    fireEvent('seen')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcement.id])

  function handleDismiss() {
    fireDismissal()
    setHidden(true) // hide in place — dismissing is an answer given here, never a navigation
  }

  // A CTA click is two independent writes: the dismissal (state — "message delivered", §4.2) and
  // the census mark (observation). Both are fired alongside the navigation, neither delays it.
  function handleLearnMore() {
    fireDismissal()
    fireEvent('learn_more_click')
  }

  function handleCta() {
    fireDismissal()
    fireEvent('cta_click')
  }

  if (hidden) return null

  const items = (announcement.body.items ?? []).slice(0, 3)
  const paragraph = announcement.body.paragraph
  const hasFooter = Boolean(announcement.learnMoreHref || announcement.tryItHref)

  return (
    <aside
      aria-label="Product announcement"
      data-testid="announcement-card"
      // ABSOLUTE, not fixed: the layout's content column is the positioning context, so the card
      // sits bottom-left of the CONTENT and stays clear of the sidebar at either width without the
      // card having to know how wide the sidebar happens to be. Below dialogs (z-50) and toasts
      // (z-[100]), above page content.
      className={cn(
        'animate-slide-in absolute bottom-4 left-4 right-4 z-30 overflow-hidden rounded-xl',
        'border border-border/60 bg-white shadow-xl shadow-slate-900/10',
        'sm:bottom-6 sm:left-6 sm:right-auto sm:w-[400px]'
      )}
    >
      <div className="relative">
        <AnnouncementCover coverHtml={announcement.coverHtml} title={announcement.title} />
        {/* Sibling of the cover, never a child: the cover wrapper is pointer-events-none. */}
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss announcement"
          className="absolute right-2 top-2 rounded-full bg-slate-900/50 p-1.5 text-white/80 backdrop-blur-sm transition-colors cursor-pointer hover:bg-slate-900/75 hover:text-white"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="p-5">
        {announcement.tagLabel && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {announcement.tagLabel}
          </p>
        )}
        <h2 className="mt-1 text-base font-semibold leading-snug tracking-tight text-foreground">
          {announcement.title}
        </h2>

        {items.length > 0 && (
          <ul className="mt-4 space-y-3.5">
            {items.map((item, index) => {
              const Icon = announcementIcon(item.icon)
              return (
                <li key={`${item.heading}-${index}`} className="flex gap-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-primary">{item.heading}</p>
                    <p className="mt-0.5 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
                  </div>
                </li>
              )
            })}
          </ul>
        )}

        {items.length === 0 && paragraph && (
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{paragraph}</p>
        )}
      </div>

      {hasFooter && (
        <div className="flex items-center gap-2 border-t border-border/40 px-5 py-3">
          {announcement.learnMoreHref && (
            <Link
              href={announcement.learnMoreHref}
              onClick={handleLearnMore}
              className={cn(
                buttonVariants({ variant: 'ghost', size: 'sm' }),
                'rounded-lg text-muted-foreground hover:text-foreground'
              )}
            >
              Learn more
            </Link>
          )}
          {announcement.tryItHref && (
            <Link
              href={announcement.tryItHref}
              onClick={handleCta}
              className={cn(buttonVariants({ size: 'sm' }), 'ml-auto rounded-lg')}
            >
              {announcement.ctaLabel || 'Take a look'}
            </Link>
          )}
        </div>
      )}
    </aside>
  )
}
